import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDate } from '@/lib/utils';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { PageHeader } from '@/components/PageHeader';
import { StatusBadge } from '@/components/StatusBadge';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Package, Plus, Download } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

interface VerificationPackage {
  id: string;
  package_id: string;
  license_id: string;
  license_key?: string;
  license_name?: string;
  encrypted_data: string;
  iv: string;
  auth_tag: string;
  status: string;
  created_at?: number;
  expires_at?: number;
}

function VerificationPackages() {
  const { t } = useTranslation();
  const [packages, setPackages] = useState<VerificationPackage[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [newPackage, setNewPackage] = useState({
    licenseKey: '',
    systemSignature: '',
  });

  useEffect(() => {
    fetchPackages();
  }, []);

  const fetchPackages = async () => {
    try {
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_URL || '';

      const response = await fetch(`${apiUrl}/api/verification`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setPackages(data.data || []);
      }
    } catch (error) {
      console.error('Failed to fetch verification packages:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreatePackage = async () => {
    try {
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_URL || '';

      const response = await fetch(`${apiUrl}/api/verification-packages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(newPackage),
      });

      if (response.ok) {
        setIsCreateDialogOpen(false);
        setNewPackage({ licenseKey: '', systemSignature: '' });
        fetchPackages();
      }
    } catch (error) {
      console.error('Failed to create verification package:', error);
    }
  };

  const handleDownloadPackage = (pkg: VerificationPackage) => {
    try {
      // Create verification package JSON with the encrypted data
      const packageData = {
        packageId: pkg.package_id,
        encryptedData: pkg.encrypted_data,
        iv: pkg.iv,
        authTag: pkg.auth_tag,
      };

      // Create blob from JSON data
      const blob = new Blob([JSON.stringify(packageData, null, 2)], { type: 'application/json' });

      // Create download link
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `verification-package-${pkg.package_id}.json`;

      // Trigger download
      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download verification package:', error);
    }
  };

  if (isLoading) {
    return (
      <div className="w-full px-4 py-6 sm:px-6 lg:px-8">
        <Card>
          <CardContent className="pt-6">
            <p className="text-center text-muted-foreground">{t('common.loading')}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="w-full space-y-6 px-4 py-6 sm:px-6 lg:px-8">
      <PageHeader
        eyebrow="License management"
        icon={Package}
        title={t('verificationPackages.title')}
        subtitle={t('verificationPackages.subtitle')}
        actions={(
          <Button onClick={() => setIsCreateDialogOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            {t('verificationPackages.createPackage')}
          </Button>
        )}
      />

      <Card>
        <CardHeader>
          <CardTitle>{t('verificationPackages.allPackages')}</CardTitle>
          <CardDescription>
            {t('verificationPackages.description', { count: packages.length })}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table className="data-table">
            <TableHeader>
              <TableRow>
                <TableHead>{t('verificationPackages.table.licenseKey')}</TableHead>
                <TableHead>{t('verificationPackages.table.packageId')}</TableHead>
                <TableHead>{t('licenses.status')}</TableHead>
                <TableHead>{t('licenses.createdAt')}</TableHead>
                <TableHead>{t('verificationPackages.table.expires')}</TableHead>
                <TableHead className="text-right">{t('users.actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {packages.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="empty-state">
                    <p>{t('dashboard.noData')}</p>
                  </TableCell>
                </TableRow>
              ) : (
                packages.map((pkg) => (
                  <TableRow key={pkg.id}>
                    <TableCell className="font-mono text-xs max-w-xs break-words">
                      {pkg.license_key}
                    </TableCell>
                    <TableCell className="font-mono text-xs max-w-xs break-words">
                      {pkg.package_id}
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={pkg.status} />
                    </TableCell>
                    <TableCell>
                      {formatDate(pkg.created_at)}
                    </TableCell>
                    <TableCell>
                      {pkg.expires_at
                        ? formatDate(pkg.expires_at)
                        : t('licenses.never')}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="sm" onClick={() => handleDownloadPackage(pkg)}>
                        <Download className="h-4 w-4 mr-1" />
                        {t('verificationPackages.download')}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create Verification Package Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('verificationPackages.dialogs.create.title')}</DialogTitle>
            <DialogDescription>
              {t('verificationPackages.dialogs.create.description')}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="licenseKey">{t('verificationPackages.dialogs.create.licenseKey')}</Label>
              <Input
                id="licenseKey"
                value={newPackage.licenseKey}
                onChange={(e) => setNewPackage({ ...newPackage, licenseKey: e.target.value })}
                placeholder={t('verificationPackages.dialogs.create.licenseKeyPlaceholder')}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="systemSignature">{t('verificationPackages.dialogs.create.systemSignature')}</Label>
              <Input
                id="systemSignature"
                value={newPackage.systemSignature}
                onChange={(e) => setNewPackage({ ...newPackage, systemSignature: e.target.value })}
                placeholder={t('verificationPackages.dialogs.create.systemSignaturePlaceholder')}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              onClick={handleCreatePackage}
              disabled={!newPackage.licenseKey || !newPackage.systemSignature}
            >
              {t('verificationPackages.createPackage')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default VerificationPackages;
